import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import { getActiveSchema } from '@/supabaseClient';
import { SchemaName } from '@/types/app.types';

interface UIState {
    lastView: string;
    isLoading: boolean;
    activeSchema: SchemaName;
    agendaViewMode: 'agenda' | 'timeline';
    calendarViewMode: 'date' | 'month';
    isMetabolicViewActive: boolean;
    lastSelectedDate: string | null;
    isSleepModeMinimized: boolean;
    isSessionMinimized: boolean;
}

interface UIActions {
    setLastView: (view: string) => void;
    setIsLoading: (loading: boolean) => void;
    setAgendaViewMode: (mode: 'agenda' | 'timeline') => void;
    setLastSelectedDate: (date: Date | string | null) => void;
    setIsSleepModeMinimized: (minimized: boolean) => void;
    setIsSessionMinimized: (minimized: boolean) => void;
    maximizeRecovery: () => void;
    maximizeSession: () => void;
    setCalendarViewMode: (mode: 'date' | 'month') => void;
    setMetabolicViewActive: (active: boolean) => void;
    getFKConstraint: () => string;
}

/**
 * UI STORE (v4)
 * Manages view-state, global loading, and schema-aware constraints.
 * HARDENED: Tracks minimized states for both recovery and training sessions.
 */
export const useUIStore = create<UIState & UIActions>()(
    persist(
        (set, get) => ({
            lastView: 'master-agenda',
            isLoading: false,
            activeSchema: getActiveSchema() as SchemaName,
            agendaViewMode: 'agenda',
            lastSelectedDate: null,
            isSleepModeMinimized: false,
            isSessionMinimized: false,

            setLastView: (view) => set({ lastView: view }),
            setIsLoading: (loading) => set({ isLoading: loading }),
            setAgendaViewMode: (mode) => set({ agendaViewMode: mode }),
            setLastSelectedDate: (date) => set({ 
                lastSelectedDate: date instanceof Date ? date.toISOString() : date 
            }),
            setIsSleepModeMinimized: (minimized) => set({ isSleepModeMinimized: minimized }),
            setIsSessionMinimized: (minimized) => set({ isSessionMinimized: minimized }),
            maximizeRecovery: () => set({ isSleepModeMinimized: false }),
            maximizeSession: () => set({ isSessionMinimized: false }),
            setCalendarViewMode: (mode) => set({ calendarViewMode: mode }),
            setMetabolicViewActive: (active) => set({ isMetabolicViewActive: active }),
            
            getFKConstraint: () => get().activeSchema === 'v3_dev' 
                ? 'dev_performance_logs_block_item_fkey' 
                : 'performance_logs_block_item_id_fkey'
        }),
        { 
            name: 'mp-ui-state',
            partialize: (state) => ({ 
                agendaViewMode: state.agendaViewMode,
                calendarViewMode: state.calendarViewMode,
                isMetabolicViewActive: state.isMetabolicViewActive,
                lastSelectedDate: state.lastSelectedDate,
                isSleepModeMinimized: state.isSleepModeMinimized,
                isSessionMinimized: state.isSessionMinimized
            })
        }
    )
);
